import { useState, useCallback, useRef, useEffect } from 'react';
import { View, GpsLocation } from '@/types';
import { requestGpsLocation, requestGpsLocationAll } from '@/services/viewService';
import { getGpsErrorMessage } from '@/utils/gpsErrorMessages';

const GPS_TIMEOUT_MS = 50_000;

// Progressive message thresholds (ms from request start)
const PROGRESS_MESSAGES: { threshold: number; message: string }[] = [
  { threshold: 0, message: 'Requesting position...' },
  { threshold: 5000, message: 'Acquiring GPS signal...' },
  { threshold: 20000, message: 'Waiting for satellite fix...' },
];

/**
 * Get the progressive loading message based on elapsed time.
 */
function getProgressMessage(elapsedMs: number): string {
  let message = PROGRESS_MESSAGES[0].message;
  for (const { threshold, message: msg } of PROGRESS_MESSAGES) {
    if (elapsedMs >= threshold) message = msg;
  }
  return message;
}

/**
 * Hook to request and track GPS location for a single view.
 * Watches view.gpsLocation and view.gpsError for updates after requesting.
 */
export function useGpsLocation(view: View) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progressMessage, setProgressMessage] = useState('');
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const progressRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const requestedAtRef = useRef<number>(0);

  const location = view.gpsLocation ?? null;
  const gpsError = view.gpsError ?? null;

  // Clear timers on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (progressRef.current) clearInterval(progressRef.current);
    };
  }, []);

  // When location updates while loading, stop loading
  useEffect(() => {
    if (isLoading && location && location.timestamp) {
      const locationTime = location.timestamp.toMillis?.() ?? location.timestamp.seconds * 1000;
      if (locationTime >= requestedAtRef.current) {
        setIsLoading(false);
        setError(null);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        if (progressRef.current) clearInterval(progressRef.current);
      }
    }
  }, [isLoading, location]);

  // When gpsError arrives while loading, show error and stop loading
  useEffect(() => {
    if (isLoading && gpsError && gpsError.timestamp) {
      const errorTime = gpsError.timestamp.toMillis?.() ?? gpsError.timestamp.seconds * 1000;
      if (errorTime >= requestedAtRef.current) {
        setIsLoading(false);
        setError(getGpsErrorMessage(gpsError.code));
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        if (progressRef.current) clearInterval(progressRef.current);
      }
    }
  }, [isLoading, gpsError]);

  const requestLocation = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setProgressMessage(PROGRESS_MESSAGES[0].message);
    requestedAtRef.current = Date.now();

    try {
      await requestGpsLocation(view.userId, view.projectId, view.viewId);

      // Progressive messages
      progressRef.current = setInterval(() => {
        const elapsed = Date.now() - requestedAtRef.current;
        setProgressMessage(getProgressMessage(elapsed));
      }, 1000);

      // Set timeout
      timeoutRef.current = setTimeout(() => {
        setIsLoading(false);
        if (progressRef.current) clearInterval(progressRef.current);
        if (location) {
          setError(null); // Has old location, show it with age
        } else {
          setError('Position not available');
        }
      }, GPS_TIMEOUT_MS);
    } catch (err) {
      setIsLoading(false);
      setError('Failed to request GPS location');
    }
  }, [view.userId, view.projectId, view.viewId, location]);

  return { location, isLoading, error, progressMessage, requestLocation };
}

/**
 * Hook to manage GPS locations for multiple views.
 * Tracks loading state, errors, and provides bulk request with progressive messages.
 */
export function useGpsLocations(views: View[]) {
  const [loadingViewIds, setLoadingViewIds] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<Map<string, string>>(new Map());
  const [progressMessages, setProgressMessages] = useState<Map<string, string>>(new Map());
  const timeoutRefs = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const progressRefs = useRef<Map<string, ReturnType<typeof setInterval>>>(new Map());
  const requestTimesRef = useRef<Map<string, number>>(new Map());

  // Build locations map from views
  const locations = new Map<string, GpsLocation>();
  for (const view of views) {
    if (view.gpsLocation) {
      locations.set(view.viewId, view.gpsLocation);
    }
  }

  // Clear timeouts on unmount
  useEffect(() => {
    return () => {
      for (const timeout of timeoutRefs.current.values()) clearTimeout(timeout);
      for (const interval of progressRefs.current.values()) clearInterval(interval);
    };
  }, []);

  // When a view's location or gpsError updates, clear its loading state
  useEffect(() => {
    for (const view of views) {
      if (!loadingViewIds.has(view.viewId)) continue;
      const requestTime = requestTimesRef.current.get(view.viewId) ?? 0;

      // Check location update
      if (view.gpsLocation) {
        const locationTime = view.gpsLocation.timestamp?.toMillis?.()
          ?? (view.gpsLocation.timestamp?.seconds ?? 0) * 1000;
        if (locationTime >= requestTime) {
          clearViewLoading(view.viewId);
          setErrors((prev) => { const next = new Map(prev); next.delete(view.viewId); return next; });
        }
      }

      // Check gpsError update
      if (view.gpsError) {
        const errorTime = view.gpsError.timestamp?.toMillis?.()
          ?? (view.gpsError.timestamp?.seconds ?? 0) * 1000;
        if (errorTime >= requestTime) {
          clearViewLoading(view.viewId);
          setErrors((prev) => {
            const next = new Map(prev);
            next.set(view.viewId, getGpsErrorMessage(view.gpsError!.code));
            return next;
          });
        }
      }
    }
  }, [views, loadingViewIds]);

  function clearViewLoading(viewId: string) {
    setLoadingViewIds((prev) => {
      const next = new Set(prev);
      next.delete(viewId);
      return next;
    });
    const timeout = timeoutRefs.current.get(viewId);
    if (timeout) { clearTimeout(timeout); timeoutRefs.current.delete(viewId); }
    const interval = progressRefs.current.get(viewId);
    if (interval) { clearInterval(interval); progressRefs.current.delete(viewId); }
    setProgressMessages((prev) => { const next = new Map(prev); next.delete(viewId); return next; });
  }

  function startViewLoading(viewId: string) {
    const now = Date.now();
    setLoadingViewIds((prev) => new Set(prev).add(viewId));
    setErrors((prev) => { const next = new Map(prev); next.delete(viewId); return next; });
    requestTimesRef.current.set(viewId, now);

    // Progressive messages
    setProgressMessages((prev) => new Map(prev).set(viewId, PROGRESS_MESSAGES[0].message));
    const interval = setInterval(() => {
      const elapsed = Date.now() - now;
      setProgressMessages((prev) => new Map(prev).set(viewId, getProgressMessage(elapsed)));
    }, 1000);
    progressRefs.current.set(viewId, interval);

    // Timeout
    const timeout = setTimeout(() => {
      clearViewLoading(viewId);
    }, GPS_TIMEOUT_MS);
    timeoutRefs.current.set(viewId, timeout);
  }

  const requestAll = useCallback(async () => {
    if (views.length === 0) return;

    for (const view of views) {
      startViewLoading(view.viewId);
    }

    // Group views by project for batch requests
    const byProject = new Map<string, { userId: string; projectId: string; viewIds: string[] }>();
    for (const view of views) {
      const key = `${view.userId}/${view.projectId}`;
      if (!byProject.has(key)) {
        byProject.set(key, { userId: view.userId, projectId: view.projectId, viewIds: [] });
      }
      byProject.get(key)!.viewIds.push(view.viewId);
    }

    try {
      const promises = Array.from(byProject.values()).map((group) =>
        requestGpsLocationAll(group.userId, group.projectId, group.viewIds)
      );
      await Promise.all(promises);
    } catch {
      // Partial failure is OK
    }
  }, [views]);

  const requestSingle = useCallback(async (view: View) => {
    startViewLoading(view.viewId);

    try {
      await requestGpsLocation(view.userId, view.projectId, view.viewId);
    } catch {
      // ignore
    }
  }, []);

  return {
    locations,
    loadingViewIds,
    errors,
    progressMessages,
    isLoading: loadingViewIds.size > 0,
    requestAll,
    requestSingle,
  };
}
